/**
 * Print-friendly color theme for QAQC PDF reports
 * Uses high-contrast colors suitable for both color and black & white printing
 */

export const PRINT_COLORS = {
  // Base colors
  background: '#ffffff',
  text: '#000000',
  grid: '#cccccc',
  gridLight: '#e5e5e5',
  
  // Status colors (QAQC specific)
  pass: '#008000',        // Green - passing QC
  warning: '#ff8c00',     // Orange - warning threshold
  fail: '#dc2626',        // Red - failure threshold
  certified: '#0066cc',   // Blue - certified values
  
  // Chart lines (multiple series)
  line1: '#0066cc',       // Blue
  line2: '#ff8c00',       // Orange
  line3: '#008000',       // Green
  line4: '#dc2626',       // Red
  line5: '#9333ea',       // Purple
  line6: '#0891b2',       // Cyan
  
  // Data visualization
  ewma: '#0066cc',        // EWMA line color
  rollingMean: '#ff8c00', // Rolling mean color
  scatter: '#4b5563',     // Default scatter point color
  
  // Background shades (for tables, sections)
  backgroundLight: '#f9fafb',
  backgroundMedium: '#f3f4f6',
  backgroundDark: '#e5e7eb',
  
  // Borders
  border: '#d1d5db',
  borderDark: '#9ca3af',
  
  // Text variations
  textLight: '#6b7280',
  textMedium: '#374151',
  textDark: '#000000',
  
  // Reference lines
  referenceLinePass: '#008000',
  referenceLineWarn: '#ff8c00',
  referenceLineFail: '#dc2626',
  referenceLineZero: '#4b5563',
};

/**
 * Get color for status
 * @param {string} status - 'pass', 'warning', 'fail'
 * @returns {string} Hex color code
 */
export const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'pass':
      return PRINT_COLORS.pass;
    case 'warning':
    case 'warn':
      return PRINT_COLORS.warning;
    case 'fail':
    case 'failure':
      return PRINT_COLORS.fail;
    default:
      return PRINT_COLORS.text;
  }
};

/**
 * Get color for element type
 * @param {string} element - 'Au', 'Ag', 'Cu'
 * @returns {string} Hex color code
 */
export const getElementColor = (element) => {
  switch (element) {
    case 'Au':
      return PRINT_COLORS.line1; // Blue
    case 'Ag':
      return PRINT_COLORS.line2; // Orange
    case 'Cu':
      return PRINT_COLORS.line3; // Green
    default:
      return PRINT_COLORS.scatter;
  }
};

/**
 * Print-friendly chart configuration defaults
 */
export const PRINT_CHART_CONFIG = {
  fontSize: {
    title: 14,
    subtitle: 12,
    axis: 10,
    tick: 9,
    legend: 10,
  },
  margin: {
    top: 24,
    right: 64,
    bottom: 76,
    left: 76,
  },
  strokeWidth: {
    thin: 1,
    normal: 2,
    thick: 3,
  },
  pointSize: {
    small: 4,
    normal: 6,
    large: 8,
  },
};

/**
 * Get responsive chart dimensions for PDF
 * @param {string} size - 'full', 'half', 'third'
 * @returns {Object} { width, height }
 */
export const getChartDimensions = (size = 'full') => {
  const A4_WIDTH_PX = 800; // Approximate for 210mm A4 at standard resolution
  
  switch (size) {
    case 'full':
      return { width: A4_WIDTH_PX - 80, height: 400 };
    case 'half':
      return { width: (A4_WIDTH_PX - 100) / 2, height: 300 };
    case 'third':
      return { width: (A4_WIDTH_PX - 120) / 3, height: 250 };
    default:
      return { width: A4_WIDTH_PX - 80, height: 400 };
  }
};

export default PRINT_COLORS;
